import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { WasteClassification } from "generated-graphql/graphql";
import { Suspense, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { prettyPrintWasteClassification } from "../../prettyPrintWasteClassification";
import { useUpdateWasteStream, useWasteStream } from "./api";

interface EditWasteStreamDialogProps {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
  hasVendorData: boolean;
}
interface UpdateWasteForm {
  name: string;
  comment: string;
  classification: WasteClassification | null;
  vendorFormCode: string;
  vendorSourceCode: string;
}

interface EditWasteStreamFormProps {
  wasteStreamId: string;
  onClose: () => void;
  hasVendorData: boolean;
}

const EditWasteStreamForm = ({
  wasteStreamId,
  onClose,
  hasVendorData,
}: EditWasteStreamFormProps) => {
  const {
    handleSubmit: handleSubmitForm,
    formState,
    reset,
    control,
    watch,
  } = useForm<UpdateWasteForm>({
    defaultValues: {
      name: "",
      comment: "",
      classification: null,
      vendorFormCode: "",
      vendorSourceCode: "",
    },
  });
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Fetch current waste stream data - no loading state needed with suspense
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);

  // Update mutation
  const [mutateUpdateWasteStream, { loading: updating }] =
    useUpdateWasteStream();

  // Initialize form with current data
  useEffect(() => {
    if (wasteStreamData?.wasteStream) {
      reset({
        name: wasteStreamData.wasteStream.name,
        comment: wasteStreamData.wasteStream.comment ?? "",
        classification: wasteStreamData.wasteStream.classification || null,
        vendorFormCode: wasteStreamData.wasteStream.vendorFormCode ?? "",
        vendorSourceCode: wasteStreamData.wasteStream.vendorSourceCode ?? "",
      });
    }
  }, [wasteStreamData, reset]);

  const performUpdate = async (data: UpdateWasteForm) => {
    try {
      const result = await mutateUpdateWasteStream({
        update: (cache, { data: responseData }) => {
          if (responseData?.updateWasteStream) {
            cache.modify({
              optimistic: true,
              broadcast: true,
              id: `WasteStream:${wasteStreamId}`,
              fields: {
                name: () => data.name ?? "",
                comment: () => data.comment ?? "",
                classification: () => data.classification ?? null,
                vendorFormCode: () => data.vendorFormCode ?? "",
                vendorSourceCode: () => data.vendorSourceCode ?? "",
              },
            });
          }
        },
        variables: {
          input: {
            id: wasteStreamId,
            name: data.name.trim(),
            comment: data.comment?.trim(),
            classification: data.classification,
            formCode: data.vendorFormCode?.trim() || null,
            sourceCode: data.vendorSourceCode?.trim() || null,
          },
        },
      });
      if (result.data?.updateWasteStream) {
        alerts.success("Waste stream updated");
        onClose();
      }
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }
    } catch (error) {
      if (error instanceof Error) {
        alerts.error("Failed to update waste stream: " + error.message);
      } else {
        alerts.error(
          "Failed to update waste stream",
          "An unknown error occurred"
        );
      }
    }
  };

  const onSubmit = async (data: UpdateWasteForm) => {
    // Check if classification has changed
    const originalClassification =
      wasteStreamData?.wasteStream?.classification || null;
    const classificationChanged =
      originalClassification !== data.classification;

    if (classificationChanged) {
      // Show confirmation dialog and break out
      setShowConfirmation(true);
      return;
    }

    // Proceed with update
    await performUpdate(data);
  };

  // Handler for when user confirms the classification change
  const handleConfirmSubmit = async () => {
    const submitter = handleSubmitForm(performUpdate);
    await submitter();
    setShowConfirmation(false);
  };

  const handleClose = () => {
    if (!updating) {
      setShowConfirmation(false);
      onClose();
    }
  };

  const handleCancelConfirmation = () => {
    handleClose();
  };

  return (
    <>
      <DialogContent>
        <Stack sx={{ pt: 1 }}>
          <FormTextField name="name" label="Name" fullWidth control={control} />
          {hasVendorData && (
            <>
              <FormTextField
                name="vendorFormCode"
                label="Vendor Form Code"
                fullWidth
                control={control}
                helperText="Editing this will update all underlying waste line records"
              />
              <FormTextField
                name="vendorSourceCode"
                label="Vendor Source Code"
                fullWidth
                control={control}
                helperText="Editing this will update all underlying waste line records"
              />
            </>
          )}
          <FormTextField
            name="comment"
            label="Comment"
            fullWidth
            control={control}
          />
          <FormSelect
            name="classification"
            label="Classification"
            hideErrors
            selectItems={Object.values(WasteClassification).map((value) => ({
              value,
              display: prettyPrintWasteClassification(value),
            }))}
            control={control}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmitForm(onSubmit)}
          variant="contained"
          disabled={updating || !formState.isDirty}
        >
          {updating ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
        header="Are you sure you want to edit this waste stream classification?"
        resources={[
          {
            resourceType: "Waste Stream Name",
            resourceName: watch("name"),
          },
          {
            resourceType: "Previous Classification",
            resourceName: prettyPrintWasteClassification(
              wasteStreamData?.wasteStream?.classification
            ),
          },
          {
            resourceType: "New Classification",
            resourceName: prettyPrintWasteClassification(
              watch("classification")
            ),
          },
        ]}
        info="Changing this classification will affect all of the line items in this waste stream."
        confirmText="Yes, Save Edits"
        cancelText="Cancel"
        loading={updating}
      />
    </>
  );
};

export const EditWasteStreamDialog = ({
  open,
  onClose,
  wasteStreamId,
  hasVendorData,
}: EditWasteStreamDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Waste Stream</DialogTitle>
      <Suspense
        fallback={
          <DialogContent>
            <Stack sx={{ pt: 1 }}>
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
              <FormTextFieldSkeleton />
            </Stack>
          </DialogContent>
        }
      >
        <EditWasteStreamForm
          wasteStreamId={wasteStreamId}
          onClose={onClose}
          hasVendorData={hasVendorData}
        />
      </Suspense>
    </Dialog>
  );
};
